import React from 'react';
import { isEmpty } from 'lodash';

import { useFormikFieldError } from '~/components/core/Formik/formikErrorUtils';
import EditableChip, { type EditableChipColors } from '~/components/core/Molecules/EditableChip';
import useOrganization from '~/components/OrganizationContext';
import type { Category } from '~/server_shared/swagger/data-contracts';
import cn from '~/Utils/cn';

interface CommunicationCategorySelectChipProps {
  value: number | null;
  onChange: (categoryId: number | null) => void;
  disabled?: boolean;
  className?: string;
  required?: boolean;
}

const NONE_VALUE = 'none';

const CommunicationCategorySelectChip: React.FC<CommunicationCategorySelectChipProps> = ({
  value,
  onChange,
  disabled,
  className,
  required = false,
}) => {
  const { communicationCategories, isLoadingCommunicationCategories, isErrorCommunicationCategories } =
    useOrganization() as {
      communicationCategories: Category[];
      isLoadingCommunicationCategories: boolean;
      isErrorCommunicationCategories: boolean;
    };

  const { hasError } = useFormikFieldError('category_id');

  const enabledCategories = communicationCategories?.filter((category) => !category.is_disabled) ?? [];
  const selectedCategory = enabledCategories.find((c) => c.id === value);
  const displayValue = selectedCategory?.key ?? (value === null ? NONE_VALUE : undefined);

  const noneOption = required
    ? []
    : [{ id: NONE_VALUE, color: 'grey-600' as EditableChipColors, description: 'None', disabled: false }];

  const options = [
    ...noneOption,
    ...enabledCategories.map(({ key, color, label, is_disabled }) => ({
      id: key,
      color: (color || 'slate-800') as EditableChipColors,
      description: label,
      disabled: is_disabled,
    })),
  ];

  const handleChange = (selectedKey: string) => {
    if (selectedKey === NONE_VALUE) {
      onChange(null);
    } else {
      const category = enabledCategories.find((c) => c.key === selectedKey);
      if (category) {
        onChange(category.id);
      }
    }
  };

  if (isLoadingCommunicationCategories || isErrorCommunicationCategories || isEmpty(enabledCategories)) {
    return null;
  }

  return (
    <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
      <EditableChip
        value={displayValue ?? NONE_VALUE}
        onChange={handleChange}
        options={options}
        disabled={disabled}
        className={cn(className, { 'border-red-700 bg-red-100 text-red-700': hasError })}
      />
    </div>
  );
};

export default CommunicationCategorySelectChip;
